<template>
  <!-- 上余白は直前の体験セクションの pb が担うため、ここでは下余白のみ持つ (Reviews/Theme/Story/Banner と統一)。
       ClientOnly には包まない: 包むと初期 HTML からセクションごと消え、ハイドレーション後に高さが伸びる。 -->
  <section class="flex flex-col items-center gap-8 bg-beige1 pb-20" data-testid="journey-through-japan-section">
    <!-- 見出しブロックは 1216px コンテナ内 (Figma 59951:7374) -->
    <div class="flex w-full flex-col gap-2 px-4 md:mx-auto md:w-11/12 md:max-w-[1216px] md:px-0">
      <div class="flex items-center gap-4">
        <!-- 外部リンクは素の <a> にする。NuxtLink だとクリックで新規タブが開かず E2E が落ちた
             (BannerSection の gta-banner-link と同じ流儀)。 -->
        <a class="flex shrink-0 items-center gap-4 capitalize" data-testid="journey-section-channel-link" rel="noopener noreferrer" target="_blank" :href="YOUTUBE_CHANNEL_URL">
          <h2 class="whitespace-nowrap font-Spectral text-[26px] font-medium leading-9 tracking-[0.5px] text-light-fg-accent md:text-[30px]" data-testid="journey-section-heading">
            {{ HEADING }}
          </h2>
          <span aria-hidden="true" class="material-symbols-sharp text-2xl leading-6 text-light-fg-accent">keyboard_arrow_right</span>
        </a>
        <!-- 見出しの右へ残り幅いっぱいに伸びる罫線 (Figma 59951:7378 は flex-[1_0_0] h-0) -->
        <span aria-hidden="true" class="h-px min-w-px flex-1 bg-light-bd-muted"></span>
      </div>
      <p class="font-Spectral text-base leading-6 text-light-fg-muted md:text-[17px]" data-testid="journey-section-description">{{ DESCRIPTION }}</p>
    </div>

    <!-- カルーセルは 1216px コンテナからはみ出して全幅で見せる (Figma 59951:7380 は幅 2658px)。
         幅を transform で計算するとレスポンシブで破綻するため、中央スナップのスクロールで実現する。

         循環させるため、先頭の前に「最後の複製」を、末尾の後に「先頭の複製」を 1 枚ずつ置く。
         複製に着いたらスクロール停止後に本物へ無音で飛ばす。これにより
           - 初期表示から左右どちらにもカードが見切れる (Figma の構図)
           - 右へ送り続けると先頭に戻る
         が両立する。複製は aria-hidden にし、testid も分けて実数と混ざらないようにする。 -->
    <div ref="scrollBox" class="journey-carousel flex w-full snap-x snap-mandatory gap-3 overflow-x-auto" data-testid="journey-carousel" @scroll.passive="onScroll">
      <JourneyVideoCard
        v-for="slide in visibleSlides"
        :key="slide.key"
        class="w-[min(890px,86vw)] shrink-0 snap-center"
        :aria-hidden="slide.isClone ? 'true' : undefined"
        :data-testid="slide.isClone ? 'journey-video-card-clone' : 'journey-video-card'"
        :inert="slide.isClone || undefined"
        :on-playing="onVideoPlaying"
        :video="slide.video"
      />
    </div>

    <!-- CTA は「いま中央にある動画」の遷移先へ送る。カードの中に置くとスクロールで一緒に流れるため外に出す。
         ctaPath を持たない動画が中央にあるときは表示しない (AC8)。 -->
    <NuxtLink
      v-if="activeVideo?.ctaPath"
      class="flex h-12 w-[368px] max-w-[calc(100%-2rem)] items-center justify-center rounded-[1px] bg-gold4 font-Spectral text-base text-white transition-opacity duration-300 hover:opacity-80"
      data-testid="journey-video-cta"
      :to="activeVideo.ctaPath"
      @click="onVideoCtaClick"
    >
      {{ CTA_LABEL }}
    </NuxtLink>

    <!-- 循環するので端でも押せる。disabled にはしない。 -->
    <div class="flex items-center gap-2">
      <button
        class="flex size-10 items-center justify-center border border-light-bd-accent text-light-fg-accent"
        data-testid="journey-carousel-prev"
        type="button"
        :aria-label="PREV_LABEL"
        @click="goTo(activeSlideIndex - 1)"
      >
        <span aria-hidden="true" class="material-symbols-sharp text-[32px] leading-none">keyboard_arrow_left</span>
      </button>
      <button
        class="flex size-10 items-center justify-center border border-light-bd-accent text-light-fg-accent"
        data-testid="journey-carousel-next"
        type="button"
        :aria-label="NEXT_LABEL"
        @click="goTo(activeSlideIndex + 1)"
      >
        <span aria-hidden="true" class="material-symbols-sharp text-[32px] leading-none">keyboard_arrow_right</span>
      </button>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { useNuxtRoute } from '@/composables/utils/useRoute';
import { sendGAWabunkaVideoCtaClickEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/videoCtaClick';
import { sendGAWabunkaVideoPlayEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/videoPlay';
import type { JourneyVideo } from '@/wabunka-src/constants/journeyThroughJapanVideos';
import { JOURNEY_THROUGH_JAPAN_VIDEOS } from '@/wabunka-src/constants/journeyThroughJapanVideos';
import JourneyVideoCard from '@/wabunka-src/features/Top/pages/components/JourneyThroughJapanSection/JourneyVideoCard.vue';

const HEADING = 'Japan Through Film';
const DESCRIPTION = 'Go behind the scenes with the artisans, artists and cultural practitioners who bring Japan’s traditions to life.';
const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@WabunkaJapan/featured';
const CTA_LABEL = 'Experience it firsthand';
const PREV_LABEL = 'Previous video';
const NEXT_LABEL = 'Next video';
const SECTION_ID = 'journey-through-japan';

const route = useNuxtRoute();

const buildVideoAnalyticsParams = (video: JourneyVideo) => {
  const position = JOURNEY_THROUGH_JAPAN_VIDEOS.findIndex(({ videoId }) => videoId === video.videoId) + 1;
  if (position === 0) return undefined;

  return {
    sectionId: SECTION_ID,
    videoId: video.videoId,
    position,
    screenName: route.name?.toString() ?? '',
  };
};

const onVideoPlaying = (video: JourneyVideo) => {
  const params = buildVideoAnalyticsParams(video);
  if (params != null) sendGAWabunkaVideoPlayEvent(params);
};

/** スクロールが止まったとみなすまでの猶予。scrollend はブラウザ対応が揃っていないため時間で判定する。 */
const SETTLE_DELAY_MS = 140;

interface Slide {
  key: string;
  video: JourneyVideo;
  isClone: boolean;
}

const slides = computed<Slide[]>(() => {
  const base: Slide[] = JOURNEY_THROUGH_JAPAN_VIDEOS.map((video) => ({ key: video.videoId, video, isClone: false }));
  const first = base[0];
  const last = base[base.length - 1];
  // 1 枚しかないときは循環させない（複製が本物と重なって意味を持たないため）。
  if (base.length < 2 || first === undefined || last === undefined) return base;
  return [{ key: 'clone-head', video: last.video, isClone: true }, ...base, { key: 'clone-tail', video: first.video, isClone: true }];
});

/**
 * 複製はマウント後にだけ描画する。
 * SSR の HTML は scrollLeft=0 で配信されるため、複製を含めたまま出すと
 * 先頭の複製（＝末尾動画）が中央に映り、CTA の遷移先と食い違ったうえ、
 * hydration 時にカード 1 枚ぶん横に飛ぶ（実測 902px）。
 * マウント後に複製を差し込み、同じフレームで先頭の実カードへスクロールを合わせることで、
 * 挿入による横ずれとスクロール移動が相殺されて見た目が動かない。
 */
const isMounted = ref(false);
const visibleSlides = computed<Slide[]>(() => (isMounted.value ? slides.value : slides.value.filter((slide) => !slide.isClone)));

const isLooping = computed(() => isMounted.value && slides.value.length > JOURNEY_THROUGH_JAPAN_VIDEOS.length);
/** 実データの先頭に対応する visibleSlides 上の位置。複製があるときだけ 1 枚ぶんずれる。 */
const firstRealIndex = computed(() => (isLooping.value ? 1 : 0));
const lastRealIndex = computed(() => (isLooping.value ? visibleSlides.value.length - 2 : visibleSlides.value.length - 1));

const scrollBox = ref<HTMLElement>();
// SSR/mount 前は複製が無く先頭実カードが index 0。mount 時に firstRealIndex へ同期する。
const activeSlideIndex = ref(0);
const activeVideo = computed(() => visibleSlides.value[activeSlideIndex.value]?.video);

const onVideoCtaClick = () => {
  const video = activeVideo.value;
  if (video?.ctaPath == null) return;

  const params = buildVideoAnalyticsParams(video);
  if (params != null) sendGAWabunkaVideoCtaClickEvent({ ...params, path: route.path });
};

let settleTimer: ReturnType<typeof setTimeout> | undefined;
/**
 * 矢印によるスクロール中は true。
 * アニメーション中の scroll イベントで「中間位置の最近傍」に上書きされると、
 * 連打しても 1 コマしか進まず CTA も 1 つ前を指したままになる。
 */
let isProgrammaticScroll = false;

const cardAt = (index: number): HTMLElement | undefined => {
  const child = scrollBox.value?.children[index];
  return child instanceof HTMLElement ? child : undefined;
};

const centerOffsetOf = (box: HTMLElement, card: HTMLElement) => card.offsetLeft + card.offsetWidth / 2 - box.clientWidth / 2;

/**
 * 指定位置へスクロールする。
 * 瞬間移動は scrollLeft 代入で行う（scrollTo({behavior:'auto'}) と等価で、
 * jsdom のように scrollTo を持たない環境でも動く）。
 * スムーズ移動だけ scrollTo を使い、無い環境では瞬間移動に落とす。
 */
const scrollBoxTo = (box: HTMLElement, left: number, smooth: boolean) => {
  if (smooth && typeof box.scrollTo === 'function') {
    box.scrollTo({ left, behavior: 'smooth' });
    return;
  }
  box.scrollLeft = left;
};

/** 容器の中心にいちばん近いカードを「いま見ている動画」とする。 */
const updateActiveIndex = () => {
  const box = scrollBox.value;
  if (box == null) return;
  const center = box.scrollLeft + box.clientWidth / 2;
  let nearest = 0;
  let nearestDistance = Number.POSITIVE_INFINITY;
  for (const [index, child] of [...box.children].entries()) {
    if (!(child instanceof HTMLElement)) continue;
    const distance = Math.abs(child.offsetLeft + child.offsetWidth / 2 - center);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = index;
    }
  }
  activeSlideIndex.value = nearest;
};

/** 複製から本物へ無音で移す。アニメーションさせると巻き戻しが見えるので behavior は auto。 */
const teleportTo = (index: number) => {
  const box = scrollBox.value;
  const card = cardAt(index);
  if (box == null || card == null) return;
  scrollBoxTo(box, centerOffsetOf(box, card), false);
  activeSlideIndex.value = index;
};

const onSettled = () => {
  if (!isLooping.value) return;
  const current = activeSlideIndex.value;
  const isCloneEdge = current === 0 || current === visibleSlides.value.length - 1;
  if (!isCloneEdge) return;
  // 複製で再生を始めている場合に飛ばすと iframe が作り直しになるため、そのときは留まる。
  if (cardAt(current)?.querySelector('iframe') != null) return;
  teleportTo(current === 0 ? lastRealIndex.value : firstRealIndex.value);
};

const scheduleSettle = () => {
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => {
    isProgrammaticScroll = false;
    updateActiveIndex();
    onSettled();
  }, SETTLE_DELAY_MS);
};

const onScroll = () => {
  if (!isProgrammaticScroll) updateActiveIndex();
  scheduleSettle();
};

const goTo = (index: number) => {
  const box = scrollBox.value;
  if (box == null) return;
  let target = index;
  // 複製の端に居るあいだ (onSettled で本物へ飛ぶまでの数百 ms) に矢印を続けて押すと
  // 範囲外を指す。同じ動画が映っている実カードへ無音で移してから 1 コマ送ることで、
  // 送りが 1 回ぶん消えるのを防ぐ。
  if (isLooping.value && (target < 0 || target >= visibleSlides.value.length)) {
    const anchor = target < 0 ? lastRealIndex.value : firstRealIndex.value;
    teleportTo(anchor);
    target = target < 0 ? anchor - 1 : anchor + 1;
  }
  const card = cardAt(target);
  if (card == null) return;
  // 送り先を先に確定させ、アニメーション中は scroll イベントで上書きさせない。
  isProgrammaticScroll = true;
  activeSlideIndex.value = target;
  scrollBoxTo(box, centerOffsetOf(box, card), true);
  // scroll イベントが 1 度も来ない場合の保険。
  scheduleSettle();
};

onMounted(async () => {
  // 複製の挿入と index の付け替えを同じ flush に入れる。
  // 別々にすると、複製が入った直後の 1 フレームだけ index 0（＝末尾動画の複製）を指し、
  // CTA の遷移先が末尾動画で描画される。
  isMounted.value = true;
  activeSlideIndex.value = firstRealIndex.value;
  await nextTick();
  // 挿入で内容が右へずれるので、同じカードが中央に留まるようスクロールを合わせ直す。
  const box = scrollBox.value;
  const card = cardAt(firstRealIndex.value);
  if (box != null && card != null) scrollBoxTo(box, centerOffsetOf(box, card), false);
});

onBeforeUnmount(() => clearTimeout(settleTimer));
</script>

<style lang="scss" scoped>
.journey-carousel {
  /* 中央スナップのため左右余白は「容器の半分 − カードの半分」。
     カード幅は Figma 実測 890px だが、viewport が狭いときは 86vw に落とす。
     ブレークポイントで切ると 768〜890px でカードが画面より広くなり中央スナップが破綻する。 */
  padding-inline: max(1rem, calc(50% - min(445px, 43vw)));

  /* スクロールバーは出さない（矢印とスワイプで操作する）。 */
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
